/**
 * La lógica del formulario de registro, aparte de React.
 *
 * Acá viven las tres reglas que no se pueden equivocar: qué falta para
 * poder enviar, cómo se arma el teléfono en E.164, y —la más importante—
 * qué campos SALEN de este dispositivo. Estar en una función pura las hace
 * verificables sin montar una pantalla.
 */
import {
  DEFAULT_LOCALE,
  DEFAULT_THEME,
  type CategoryLevel,
  type IdDocumentType,
  type RegisterRequest,
  type Sex,
  type SupportedLocale,
  type Theme,
} from '@tennisup/contracts';

import { DEFAULT_PHONE_COUNTRY, findCountry } from '../lib/countries';

/**
 * Lo que la pantalla tiene en la mano. NO es `RegisterRequest`: tiene dos
 * campos de más que existen solo acá (`passwordConfirm`, y el país y el
 * número de WhatsApp por separado) y que se convierten al armar la
 * petición.
 */
export interface RegisterFormValues {
  displayName: string;
  fullName: string;
  idDocumentType: IdDocumentType;
  idDocumentNumber: string;
  dateOfBirth: string;
  nationalityCode: string;
  /**
   * Vacío hasta que la persona elija. NO tiene valor por defecto: poner
   * «masculino» de entrada hace que la mitad de la gente se registre en la
   * categoría equivocada sin haber tocado el campo.
   */
  sex: Sex | '';
  provinceCode: string;
  cantonCode: string;
  districtCode: string;
  addressLine: string;
  email: string;
  password: string;
  /** Solo vive en este dispositivo. Ver `buildRegisterRequest`. */
  passwordConfirm: string;
  /** ISO alpha-2 del país del teléfono, no de la nacionalidad. */
  whatsappCountry: string;
  /** El número nacional, sin prefijo. */
  whatsappNumber: string;
  availableForChallenges: boolean;
  emailNotifications: boolean;
  /** El nivel que uno dice tener. `''` es «prefiero no decirlo». */
  declaredLevel: CategoryLevel | '';
  /**
   * LA FOTO, OPCIONAL. Vive en memoria hasta que la cuenta exista: el
   * endpoint que la recibe exige sesión, y en el registro todavía no hay.
   * La sube `RegisterPage` apenas el registro devuelve el token.
   */
  photo: File | null;
  /**
   * La ubicación del aparato, si la persona apretó el botón.
   *
   * Las dos juntas o ninguna: media coordenada no ubica a nadie, y el
   * servidor la rechaza. Por eso son un solo campo y no dos.
   */
  location: { latitude: number; longitude: number } | null;
  locale: SupportedLocale;
  theme: Theme;
}

export const MIN_PASSWORD_LENGTH = 8;

export function emptyRegisterForm(locale: SupportedLocale = DEFAULT_LOCALE, theme: Theme = DEFAULT_THEME): RegisterFormValues {
  return {
    displayName: '',
    fullName: '',
    idDocumentType: 'CEDULA',
    idDocumentNumber: '',
    dateOfBirth: '',
    // Vacía a propósito: la nacionalidad la elige la persona y no se deduce
    // del idioma ni del teléfono (ver el contrato).
    nationalityCode: '',
    sex: '',
    provinceCode: '',
    cantonCode: '',
    districtCode: '',
    addressLine: '',
    email: '',
    password: '',
    passwordConfirm: '',
    whatsappCountry: DEFAULT_PHONE_COUNTRY,
    whatsappNumber: '',
    // Encendido de entrada: quien se registra en una app de retos quiere
    // recibirlos, y apagarlo es un clic.
    availableForChallenges: true,
    emailNotifications: true,
    declaredLevel: '',
    photo: null,
    location: null,
    locale,
    theme,
  };
}

/**
 * Arma el E.164 con el país elegido en el selector.
 *
 * No se le quita un cero inicial: en Italia el cero forma parte del número
 * nacional y quitarlo produce un teléfono que no existe. Lo único que se
 * descarta son los separadores que la gente escribe (espacios, guiones,
 * paréntesis).
 */
export function toE164(countryCode: string, nationalNumber: string): string {
  const dial = findCountry(countryCode)?.dial ?? '';
  return `+${dial}${nationalNumber.replace(/\D/g, '')}`;
}

/**
 * La cascada de ubicación, como transformaciones puras del formulario.
 *
 * Están acá y no dentro del componente porque lo que garantizan no se ve en
 * la pantalla: al cambiar de provincia, el `<select>` de distrito se queda
 * sin opciones y el navegador muestra vacío igual, pero el valor viejo
 * puede seguir en el estado y viajar en la petición. Un distrito de San
 * José colgando de Guanacaste no es un dato feo, es un dato imposible.
 * Como función pura se puede afirmar sobre el estado y no sobre lo que el
 * `<select>` alcanzó a dibujar.
 */
export function withProvince(values: RegisterFormValues, provinceCode: string): RegisterFormValues {
  return { ...values, provinceCode, cantonCode: '', districtCode: '' };
}

export function withCanton(values: RegisterFormValues, cantonCode: string): RegisterFormValues {
  return { ...values, cantonCode, districtCode: '' };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Claves de traducción por campo. Vacío = se puede enviar. */
export type RegisterFormErrors = Partial<Record<keyof RegisterFormValues, string>>;

export function validateRegisterForm(values: RegisterFormValues, today: Date = new Date()): RegisterFormErrors {
  const errors: RegisterFormErrors = {};

  if (values.displayName.trim() === '') errors.displayName = 'validation.required';
  if (values.fullName.trim() === '') errors.fullName = 'validation.required';
  if (values.idDocumentNumber.trim() === '') errors.idDocumentNumber = 'validation.required';
  if (values.nationalityCode === '') errors.nationalityCode = 'validation.required';
  if (values.sex === '') errors.sex = 'validation.required';
  if (values.declaredLevel === '') errors.declaredLevel = 'validation.required';
  /*
   * LA FOTO NO SE EXIGE. Javier: «al crear la cuenta no hacer obligatorio
   * subir la foto».
   *
   * Era la única cosa obligatoria del formulario que no hace falta para
   * jugar: sin cédula no se puede inscribir a un torneo y sin categoría no
   * hay ranking, pero sin foto se juega igual. Y era lo primero de la
   * pantalla, o sea el primer «no puedo seguir» de alguien que todavía no
   * tiene cuenta —y el más caro, porque pide salir a buscar una foto—.
   *
   * Se sigue pudiendo subir acá, y desde el perfil después.
   */

  if (values.email.trim() === '') errors.email = 'validation.required';
  else if (!EMAIL_RE.test(values.email.trim())) errors.email = 'validation.email';

  if (values.password === '') errors.password = 'validation.required';
  else if (values.password.length < MIN_PASSWORD_LENGTH) errors.password = 'validation.passwordShort';

  if (values.passwordConfirm === '') errors.passwordConfirm = 'validation.required';
  else if (values.passwordConfirm !== values.password) errors.passwordConfirm = 'validation.passwordMismatch';

  if (values.dateOfBirth === '') {
    errors.dateOfBirth = 'validation.required';
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(values.dateOfBirth)) {
    errors.dateOfBirth = 'validation.birthInvalid';
  } else {
    // Fecha civil, no instante: se compara texto contra texto para que un
    // huso horario no corra el cumpleaños un día.
    const parsed = new Date(`${values.dateOfBirth}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== values.dateOfBirth) {
      errors.dateOfBirth = 'validation.birthInvalid';
    } else if (values.dateOfBirth > localDateString(today)) {
      errors.dateOfBirth = 'validation.birthFuture';
    }
  }

  const digits = values.whatsappNumber.replace(/\D/g, '');
  if (values.whatsappNumber.trim() === '') {
    errors.whatsappNumber = 'validation.required';
  } else if (digits.length < 4 || !/^[\d\s()+-]+$/.test(values.whatsappNumber)) {
    errors.whatsappNumber = 'validation.phoneDigits';
  }

  // La ubicación es obligatoria y completa. Se marca CADA select vacío y no
  // solo el primero: el error tiene que estar donde está el control que hay
  // que llenar, si no la persona baja, arregla uno, vuelve a enviar y le
  // aparece el siguiente.
  if (values.provinceCode === '') errors.provinceCode = 'validation.required';
  if (values.cantonCode === '') errors.cantonCode = 'validation.required';
  if (values.districtCode === '') errors.districtCode = 'validation.required';

  return errors;
}

export function canSubmitRegister(values: RegisterFormValues, today: Date = new Date()): boolean {
  return Object.keys(validateRegisterForm(values, today)).length === 0;
}

/**
 * De lo que hay en pantalla a lo que viaja por la red.
 *
 * `passwordConfirm` NO está acá, y esa ausencia es el punto: la
 * confirmación es un guardia contra un dedazo, no un dato de
 * autenticación. Mandarla le daría al servidor una segunda copia de la
 * contraseña que no necesita, que terminaría en un log de una petición
 * fallida. Por eso la petición se arma campo por campo y no con un spread
 * del formulario: un campo nuevo en el formulario no se cuela solo.
 */
export function buildRegisterRequest(values: RegisterFormValues): RegisterRequest {
  const address = values.addressLine.trim();

  return {
    email: values.email.trim(),
    password: values.password,
    displayName: values.displayName.trim(),
    fullName: values.fullName.trim(),
    idDocumentType: values.idDocumentType,
    // Solo se recorta: un pasaporte con letras y guiones se guarda como
    // está escrito, normalizarlo a dígitos destruye lo que no se puede
    // reconstruir.
    idDocumentNumber: values.idDocumentNumber.trim(),
    dateOfBirth: values.dateOfBirth,
    nationalityCode: values.nationalityCode,
    // `canSubmitRegister` ya garantizó que no está vacío; el `as` es lo que
    // le dice eso al compilador, que no puede seguir esa cadena.
    sex: values.sex as Exclude<RegisterFormValues['sex'], ''>,
    provinceCode: values.provinceCode,
    cantonCode: values.cantonCode,
    districtCode: values.districtCode,
    addressLine: address === '' ? null : address,
    whatsappPhone: toE164(values.whatsappCountry, values.whatsappNumber),
    availableForChallenges: values.availableForChallenges,
    emailNotifications: values.emailNotifications,
    // Obligatorio: `validateRegisterForm` no deja enviar con '' .
    declaredLevel: values.declaredLevel as CategoryLevel,
    // Las dos o ninguna, nunca una. Sin ubicación no se manda el campo: el
    // contrato lo tiene opcional, y un `null` explícito diría lo mismo pero
    // obligaría a que el servidor distinga «no la dio» de «la borró», que
    // acá no existe.
    ...(values.location === null ? {} : values.location),
    locale: values.locale,
    theme: values.theme,
  };
}

/** `YYYY-MM-DD` en la zona horaria de quien mira la pantalla. */
function localDateString(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
